import React, { useEffect, useState, useCallback } from 'react';
import { Star, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const StarRow = ({ value = 0, size = 16, onSelect = null }) => {
  const [hover, setHover] = useState(0);
  const stars = [1, 2, 3, 4, 5];
  const active = hover || value;
  return (
    <div className="inline-flex items-center gap-0.5" data-testid="review-stars">
      {stars.map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onSelect}
          onClick={onSelect ? () => onSelect(n) : undefined}
          onMouseEnter={onSelect ? () => setHover(n) : undefined}
          onMouseLeave={onSelect ? () => setHover(0) : undefined}
          className={`${onSelect ? 'cursor-pointer' : 'cursor-default'} p-0 bg-transparent border-0`}
          aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
          data-testid={onSelect ? `star-select-${n}` : undefined}
        >
          <Star
            size={size}
            className={n <= active ? 'fill-primary stroke-primary' : 'stroke-muted-foreground/50'}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewForm = ({ productId, existing, onSubmitted }) => {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [title, setTitle] = useState(existing?.title || '');
  const [comment, setComment] = useState(existing?.comment || '');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating || rating < 1 || rating > 5) {
      toast.error('Sélectionnez une note (1 à 5 étoiles).');
      return;
    }
    if (title.trim().length < 3) {
      toast.error('Le titre doit faire au moins 3 caractères.');
      return;
    }
    if (comment.trim().length < 10) {
      toast.error('Le commentaire doit faire au moins 10 caractères.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/products/${productId}/reviews`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, title: title.trim(), comment: comment.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Envoi de l\'avis impossible.');
      }
      toast.success(existing
        ? 'Avis mis à jour. Il sera à nouveau modéré avant publication.'
        : 'Merci ! Votre avis a été envoyé et sera publié après modération.');
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'envoi.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 border border-border p-5" data-testid="review-form">
      <div>
        <label className="block text-sm font-medium mb-2">Votre note</label>
        <StarRow value={rating} size={24} onSelect={setRating} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Titre</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex : Excellent produit, service au top"
          maxLength={120}
          data-testid="review-title-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Votre avis</label>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={4}
          maxLength={2000}
          placeholder="Partagez votre expérience..."
          data-testid="review-comment-input"
        />
        <p className="text-xs text-muted-foreground mt-1">
          {comment.length}/2000 — Votre avis sera modéré avant publication.
        </p>
      </div>
      {existing?.status === 'pending' && (
        <p className="text-xs text-amber-600">
          Votre avis précédent est en attente de modération. Une nouvelle soumission le remplacera.
        </p>
      )}
      {existing?.status === 'rejected' && (
        <p className="text-xs text-red-600">
          Votre avis précédent a été rejeté. Vous pouvez le corriger et le renvoyer.
        </p>
      )}
      <Button
        type="submit"
        disabled={submitting}
        className="rounded-none"
        data-testid="review-submit-button"
      >
        {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {existing ? 'Mettre à jour mon avis' : 'Envoyer mon avis'}
      </Button>
    </form>
  );
};

const ReviewItem = ({ review }) => {
  const date = review.created_at ? new Date(review.created_at).toLocaleDateString('fr-FR') : '';
  return (
    <li className="border-b border-border py-4" data-testid="review-item">
      <div className="flex items-center gap-3 mb-2">
        <StarRow value={review.rating} size={14} />
        <span className="text-sm font-medium">{review.user_name}</span>
        {date && <span className="text-xs text-muted-foreground">· {date}</span>}
      </div>
      <h4 className="font-semibold text-sm mb-1">{review.title}</h4>
      <p className="text-sm text-muted-foreground whitespace-pre-line">{review.comment}</p>
    </li>
  );
};

const ProductReviews = ({ productId }) => {
  const { user } = useAuth();
  const isAuth = user && user !== false;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ aggregate: { count: 0, average: null }, reviews: [] });
  const [myReview, setMyReview] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/products/${productId}/reviews`);
      if (res.ok) setData(await res.json());
      if (isAuth) {
        const meRes = await fetch(`${API_BASE}/products/${productId}/reviews/me`, {
          credentials: 'include',
        });
        if (meRes.ok) {
          const j = await meRes.json();
          setMyReview(j.review || null);
        }
      } else {
        setMyReview(null);
      }
    } finally {
      setLoading(false);
    }
  }, [productId, isAuth]);

  useEffect(() => { load(); }, [load]);

  const { aggregate, reviews } = data;
  const hasReviews = aggregate.count > 0;

  return (
    <section className="mt-12" data-testid="product-reviews-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Avis clients</h2>
        {hasReviews && (
          <div className="flex items-center gap-2" data-testid="reviews-aggregate">
            <StarRow value={Math.round(aggregate.average)} size={16} />
            <span className="text-sm font-medium">{aggregate.average}/5</span>
            <span className="text-xs text-muted-foreground">
              ({aggregate.count} avis)
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Chargement…</div>
      ) : (
        <>
          {!hasReviews && (
            <p className="text-sm text-muted-foreground mb-6">
              Aucun avis publié pour l'instant. Soyez le premier à partager votre expérience !
            </p>
          )}

          {hasReviews && (
            <ul className="mb-8">
              {reviews.map((r) => <ReviewItem key={r.id} review={r} />)}
            </ul>
          )}

          {/* Submit / edit block */}
          {!isAuth && (
            <div className="border border-dashed border-border p-4 text-sm text-muted-foreground">
              Connectez-vous pour laisser un avis sur ce produit.
            </div>
          )}

          {isAuth && myReview && !showForm && (
            <div className="border border-border p-4" data-testid="my-review-status">
              <p className="text-sm font-medium mb-1">Votre avis</p>
              <div className="flex items-center gap-3 mb-2">
                <StarRow value={myReview.rating} size={14} />
                <span className="text-xs text-muted-foreground">
                  Statut : {myReview.status === 'approved' ? 'Publié' :
                            myReview.status === 'rejected' ? 'Rejeté' : 'En attente de modération'}
                </span>
              </div>
              <p className="text-sm mb-3">{myReview.title}</p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(true)}
                className="rounded-none"
                data-testid="edit-review-button"
              >
                Modifier mon avis
              </Button>
            </div>
          )}

          {isAuth && (!myReview || showForm) && (
            <ReviewForm
              productId={productId}
              existing={myReview}
              onSubmitted={() => {
                setShowForm(false);
                load();
              }}
            />
          )}
        </>
      )}
    </section>
  );
};

export default ProductReviews;
